import { Flex, Text, useToast } from '@chakra-ui/react'
import { Button, ExclamationTriangleIcon, IconButton, XMarkIcon } from 'components'
import { useWorkspace } from 'components/providers'
import { generatePath, useNavigate } from 'react-router-dom'
import { SubscriptionPlan } from 'types'
import { RoutePath } from '../application-explorer/src/utils/routing'

export type LimitedResource = 'events' | 'facilities' | 'templates' | 'users'

export const resourceLimitsByPlan: Record<SubscriptionPlan, Record<LimitedResource, number>> = {
  basic: {
    events: 3,
    facilities: 1,
    templates: 1,
    users: 3,
  },
  pro: {
    events: 10,
    facilities: 3,
    templates: 3,
    users: 10,
  },
  enterprise: {
    events: Infinity,
    facilities: Infinity,
    templates: Infinity,
    users: Infinity,
  },
}

export function useCreateLimitedResource() {
  const toast = useToast()
  const navigate = useNavigate()
  const { countByCollection, userCount, workspace } = useWorkspace()

  function atMax(resource: LimitedResource) {
    const subscriptionPlan = workspace?.subscription?.plan
    if (!countByCollection || userCount == null || !subscriptionPlan) return false

    const limitByResource = resourceLimitsByPlan[subscriptionPlan]
    if (resource === 'users') return userCount >= limitByResource.users

    return countByCollection[resource] >= limitByResource[resource]
  }

  function showMaxToast(resource: string) {
    toast({
      render: ({ onClose }) => (
        <Flex
          borderRadius="md"
          boxShadow="2xl"
          gap={2}
          backgroundColor="danger.100"
          color="danger.600"
          p={4}
        >
          <ExclamationTriangleIcon boxSize={6} />

          <Flex direction="column" gap={2}>
            <Text textStyle="bodyMedium">You have reached limit for {resource}</Text>
            <Text textStyle="bodyXSmall">
              Please upgrade your account or remove current {resource} to add more.
            </Text>
            <Button
              alignSelf="flex-start"
              color="danger.600"
              variant="solid"
              onClick={() => {
                navigate({
                  pathname: generatePath(RoutePath.Subscription, { workspaceId: workspace!.id }),
                })
                onClose()
              }}
            >
              Manage Subscription
            </Button>
          </Flex>

          <IconButton
            ml={3}
            size="sm"
            align="right"
            justify="top"
            icon={<XMarkIcon color="black" />}
            aria-label="close"
            onClick={() => onClose()}
          />
        </Flex>
      ),
    })
  }

  return { atMax, showMaxToast }
}

export function useLimitedResourceCounts() {
  let limitCounts: number | undefined
  let warningCounts: number | undefined

  const { countByCollection, userCount, workspace } = useWorkspace()
  const plan = workspace?.subscription?.plan
  if (!plan || !countByCollection || userCount == null) return { limitCounts, warningCounts }

  const limitByResource = resourceLimitsByPlan[plan]
  limitCounts = 0
  warningCounts = 0

  if (userCount >= limitByResource.users) limitCounts++
  else if (userCount >= limitByResource.users - 1) warningCounts++

  if (countByCollection.events >= limitByResource.events) limitCounts++
  else if (countByCollection.events >= limitByResource.events - 1) warningCounts++

  if (countByCollection.facilities >= limitByResource.facilities) limitCounts++
  else if (countByCollection.facilities >= limitByResource.facilities - 1) warningCounts++

  if (countByCollection.templates >= limitByResource.templates) limitCounts++
  else if (countByCollection.templates >= limitByResource.templates - 1) warningCounts++

  return { limitCounts, warningCounts }
}
